import {
  canonicalTargetRatios,
  classifyAdPlatform,
  defaultTargetRatiosForPlatforms,
  type TargetRatio,
  type TargetRatioPlatformFamily,
} from "@nexus/contracts"
import * as React from "react"

interface UseTargetRatioSelectionInput {
  open: boolean
  adPlatforms: readonly string[] | string | null | undefined
  platformsReady: boolean
  /** A selection restored on mount (e.g. a retried send). It wins over the platform default. */
  initialSelected?: readonly TargetRatio[]
}

/**
 * Applies the platform default once per open, then keeps the user's toggles.
 * Defaults wait until the platform list is ready so a late load cannot flash
 * the empty-selection error.
 */
export function useTargetRatioSelection({
  open,
  adPlatforms,
  platformsReady,
  initialSelected,
}: UseTargetRatioSelectionInput): {
  selected: TargetRatio[]
  toggle: (ratio: TargetRatio) => void
  family: TargetRatioPlatformFamily
  isReady: boolean
  showEmptyError: boolean
} {
  const restoredRatios = open ? canonicalTargetRatios(initialSelected ?? []) : []
  const restored = restoredRatios.length > 0
  const startsReady = open && platformsReady
  const [selected, setSelected] = React.useState<TargetRatio[]>(() =>
    restored
      ? restoredRatios
      : startsReady
        ? defaultTargetRatiosForPlatforms(adPlatforms)
        : [],
  )
  const [applied, setApplied] = React.useState(startsReady || restored)
  const editedRef = React.useRef(restored)
  const platformsKey = platformKey(adPlatforms)

  React.useEffect(() => {
    if (!open) {
      editedRef.current = false
      setApplied(false)
      setSelected((current) => (current.length === 0 ? current : []))
      return
    }
    if (!platformsReady || editedRef.current) return
    const next = defaultTargetRatiosForPlatforms(adPlatforms)
    setSelected((current) =>
      current.length === next.length &&
      current.every((ratio, index) => ratio === next[index])
        ? current
        : next,
    )
    setApplied(true)
  }, [adPlatforms, open, platformsKey, platformsReady])

  const toggle = React.useCallback((ratio: TargetRatio) => {
    editedRef.current = true
    setApplied(true)
    setSelected((current) =>
      canonicalTargetRatios(
        current.includes(ratio)
          ? current.filter((item) => item !== ratio)
          : [...current, ratio],
      ),
    )
  }, [])

  const isReady = open && platformsReady && applied
  return {
    selected,
    toggle,
    family: classifyAdPlatform(adPlatforms),
    isReady,
    showEmptyError: isReady && selected.length === 0,
  }
}

function platformKey(
  adPlatforms: readonly string[] | string | null | undefined,
): string {
  if (typeof adPlatforms === "string") return adPlatforms
  if (adPlatforms == null) return ""
  return adPlatforms.join("\0")
}
